import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import DocxPreviewModal from "../../shared/components/DocxPreviewModal";
import { StyledButton } from "../../shared/styled/TitleBar";
import { ProductExport, ProductImportInfo } from "../../data/entities";
import { buildDocxData } from "../../utils/docxBuilder";
import {
    productExportHistoryDownloadDescriptor,
    productImportHistoryDownloadDescriptor,
} from "../../utils/docxMappings";
import { generateDocument, generateDocumentBlob } from "../../utils/exportUtils";
import { error } from "../../utils/toast";
import apiMethods from "../../utils/api";
import ImportTemplate from "../../templates/phieu_nhap_kho_san_pham.docx";
import ExportTemplate from "../../templates/PMS - Phiếu xuất kho thành phẩm.docx";

type DocumentKind = "import" | "export";
type DocumentItem = ProductImportInfo | ProductExport;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EXPORT_ID_PATTERN = /^[1-9][0-9]*$/;

const ProductHistoryDeepLinkPreview = ({ kind }: { kind: DocumentKind }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [item, setItem] = useState<DocumentItem | null>(null);
    const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const searchRef = useRef(location.search);
    searchRef.current = location.search;
    const expectedTab = kind === "import" ? "product-import-history" : "product-export-history";
    const previewId = new URLSearchParams(location.hash.slice(1)).get("preview");
    const isPreviewRoute = new URLSearchParams(location.search).get("tab") === expectedTab && Boolean(previewId);
    const descriptor = kind === "import"
        ? productImportHistoryDownloadDescriptor
        : productExportHistoryDownloadDescriptor;
    const template = kind === "import" ? ImportTemplate : ExportTemplate;

    useEffect(() => {
        if (!isPreviewRoute || !previewId) return;
        setItem(null);
        setPreviewBlob(null);
        setLoadingError(null);
        const clearDeepLink = () => navigate(`${location.pathname}${searchRef.current}`, { replace: true });
        const validId = kind === "import"
            ? UUID_PATTERN.test(previewId)
            : EXPORT_ID_PATTERN.test(previewId);
        if (!validId) {
            error("Không thể xem trước phiếu", "ID chứng từ không hợp lệ.");
            clearDeepLink();
            return;
        }

        let cancelled = false;
        const loadPreview = async () => {
            try {
                const result = (await apiMethods.get(`/products/${kind}/${previewId}/`)) as DocumentItem;
                const blob = await generateDocumentBlob(buildDocxData(result, descriptor), template);
                if (!cancelled) {
                    setItem(result);
                    setPreviewBlob(blob);
                }
            } catch (requestError) {
                if (!cancelled) {
                    setLoadingError("Không thể tải phiếu xem trước. Vui lòng thử lại.");
                    error("Không thể xem trước phiếu", requestError);
                }
            }
        };
        loadPreview();
        return () => { cancelled = true; };
    }, [kind, expectedTab, isPreviewRoute, previewId, retryCount, location.pathname, navigate, descriptor, template]);

    const closePreview = () => {
        setItem(null);
        setPreviewBlob(null);
        setLoadingError(null);
        navigate(`${location.pathname}${location.search}`, { replace: true });
    };

    const retryPreview = () => {
        setLoadingError(null);
        setRetryCount((count) => count + 1);
    };

    const download = () => {
        if (!item) return;
        const date = kind === "import"
            ? (item as ProductImportInfo).import_date
            : (item as ProductExport).export_date;
        const filename = kind === "import"
            ? `phieu_nhap_kho_${new Date(date).toLocaleDateString("en-GB")}`
            : `phieu_xuat_kho_${(item as ProductExport).storage}_${new Date(date).toLocaleDateString("en-GB")}`;
        generateDocument(buildDocxData(item, descriptor), template, filename);
    };

    return (
        <DocxPreviewModal
            open={isPreviewRoute}
            onClose={closePreview}
            blob={previewBlob}
            title={kind === "import" ? "Xem trước phiếu nhập kho" : "Xem trước phiếu xuất kho"}
            debug={false}
            loadingError={loadingError}
            onRetry={retryPreview}
            actions={
                <StyledButton variant="outlined" onClick={download}>
                    <DownloadIcon style={{ marginRight: 8 }} />
                    Tải phiếu
                </StyledButton>
            }
        />
    );
};

export default ProductHistoryDeepLinkPreview;
